import { useEffect, useRef, useState } from "react";

type HeroBackdropVideoProps = {
  src: string;
  /** e.g. `center 48%` to match a photo crop */
  objectPosition?: string;
  className?: string;
  onError?: () => void;
};

/**
 * Full-bleed muted loop video for hero sections (mobile + desktop).
 * No poster image — parent supplies a solid/gradient backdrop to avoid flash on load.
 * Respects prefers-reduced-motion (no autoplay / no loop).
 */
export function HeroBackdropVideo({
  src,
  objectPosition = "center",
  className = "",
  onError,
}: HeroBackdropVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    setVideoReady(false);
  }, [src]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (prefersReducedMotion) {
      el.pause();
      return;
    }
    el.play().catch(() => {
      /* autoplay policy — gradient backdrop remains */
    });
  }, [prefersReducedMotion, src]);

  const markReady = () => setVideoReady(true);

  return (
    <video
      ref={videoRef}
      src={src}
      className={`absolute left-1/2 top-1/2 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 object-cover transition-opacity duration-500 ease-out ${
        videoReady ? "opacity-100" : "opacity-0"
      } ${className}`}
      style={{
        minWidth: "100%",
        minHeight: "100%",
        width: "auto",
        height: "auto",
        objectPosition,
      }}
      autoPlay={!prefersReducedMotion}
      muted
      loop={!prefersReducedMotion}
      playsInline
      preload="auto"
      aria-hidden
      onLoadedData={markReady}
      onPlaying={markReady}
      onError={onError}
    />
  );
}
